import globalStore from '../../../store/main';
import { checkPricingAccess, getAccessToDefaultColumns } from '../../../helpers/pricingHelper';
import { getPreparedCustomColumnById } from './CustomColumn';
import projectsModel from '../../../models/projects';
import rights from '../../../components/rights';

const costColumns = [
  {
    id: 'total_price',
    property: 'total_price',
    text: __('gantt_grid_total_price'),
    tooltip: __('total_price_custom_grid_tooltip'),
    extra: {
      helper: 'cost',
    },
    disabled: true,
    editor: false,
    align: 'center',
    resizable: true,
  },
  {
    id: 'actual_cost',
    property: 'actual_cost',
    text: __('gantt_grid_actual_cost'),
    tooltip: __('actual_cost_custom_grid_tooltip'),
    disabled: true,
    editor: false,
    align: 'center',
    resizable: true,
  },
];

const prependColumns = [
  {
    id: 'comments',
    property: 'comments',
    text: __('gantt_task_comments'),
    tooltip: __('comments_custom_grid_tooltip'),
    width: 24,
    editor: 'icon',
    iconName: 'message',
    emptyHeader: true,
    resizable: false,
    hardWidth: true,
    stickyLeft: 1,
  },
  {
    id: 'attachments',
    property: 'attachments',
    text: __('gantt_grid_attachments'),
    tooltip: __('attachments_custom_grid_tooltip'),
    width: 22,
    editor: 'icon',
    iconName: 'clip',
    emptyHeader: true,
    resizable: false,
    hardWidth: true,
    stickyLeft: 2,
  },
];

const defaultColumns = [
  {
    id: 'project_name',
    property: 'project_name', // project_name
    propertyExtra: 'project_extra',
    text: __('gantt_grid_project_name'),
    tooltip: __('project_name_custom_grid_tooltip'),
    editor: 'project',
    extra: {
      text: __('gantt_grid_option_project_name'),
      name: 'project_name_extra',
    },
    resizable: true,
  },
  {
    id: 'start_date',
    property: 'start_date',
    text: __('gantt_grid_start_date'),
    tooltip: __('start_date_custom_grid_tooltip'),
    editor: 'datepicker',
    disabled: false,
    extra: {
      text: __('gantt_grid_option_start_date'),
      name: 'start_date_extra',
    },
    resizable: true,
  },
  {
    id: 'end_date',
    property: 'end_date',
    text: __('gantt_grid_end_date'),
    tooltip: __('end_date_custom_grid_tooltip'),
    editor: 'datepicker',
    disabled: false,
    extra: {
      text: __('gantt_grid_option_end_date'),
      name: 'end_date_extra',
    },
    resizable: true,
  },
  {
    id: 'duration',
    property: 'duration',
    text: __('gantt_grid_duration'),
    tooltip: __('duration_custom_grid_tooltip'),
    editor: 'text',
    type: 'time',
    resizable: true,
  },
  {
    id: 'progress',
    property: 'progress',
    text: __('gantt_grid_progress'),
    tooltip: __('progress_custom_grid_tooltip'),
    editor: 'number',
    type: 'percent',
    resizable: true,
  },
  {
    id: 'estimation',
    property: 'estimation',
    text: __('gantt_grid_estimation'),
    tooltip: __('estimation_custom_grid_tooltip'),
    editor: 'text',
    type: 'time',
    resizable: true,
  },
  {
    id: 'assigned',
    property: 'assigned', // resource_id
    text: __('gantt_grid_assigned'),
    tooltip: __('assigned_custom_grid_tooltip'),
    editor: 'resources',
    resizable: true,
  },
  {
    id: 'status',
    property: 'status',
    text: __('gantt_grid_status'),
    tooltip: __('status_custom_grid_tooltip'),
    editor: 'selectList',
    listType: 'status',
    resizable: true,
    forJiraProject: {},
  },
  {
    id: 'priority',
    property: 'priority',
    text: __('gantt_grid_priority'),
    tooltip: __('priority_custom_grid_tooltip'),
    editor: 'selectList',
    listType: 'priority',
    resizable: true,
    forJiraProject: {},
  },
  {
    id: 'creator',
    property: 'creator', // creator
    text: __('gantt_grid_creator'),
    tooltip: __('creator_custom_grid_tooltip'),
    disabled: true,
    editor: false,
    align: 'left',
    resizable: true,
  },
  {
    id: 'created_at',
    property: 'created_at', // create_date
    text: __('gantt_grid_create_date'),
    tooltip: __('create_date_custom_grid_tooltip'),
    editor: 'datepicker',
    disabled: true,
    resizable: true,
  },
  {
    id: 'time_tracking',
    property: 'time_tracking',
    text: __('gantt_grid_time_tracking'),
    tooltip: __('time_tracking_custom_grid_tooltip'),
    editor: 'timelog',
    resizable: true,
  },
];

const staticColumns = [
  {
    id: 'text',
    width: 200,
    property: 'text',
    text: __('gantt_grid_task_name'),
    editor: 'task',
    resizable: true,
  },
];

class TableColumns {
  constructor() {
    this.isProjectType = false;
    this.ganttIDs = [];
    this.showColumns = [];
    this.dynamicColumns = [];
    this.staticColumns = staticColumns;
    this.prependColumns = prependColumns;
    this.config = null;

    this.statusCol = defaultColumns.find(i => i.id === 'status');
    this.priorityCol = defaultColumns.find(i => i.id === 'priority');
  }

  get pricingAccesToDefaultColumns() {
    return getAccessToDefaultColumns([...prependColumns, ...defaultColumns]);
  }

  get pricingAccesToCustomColumns() {
    return checkPricingAccess('custom_fields');
  }

  get pricingAccesToCost() {
    return checkPricingAccess('costs');
  }

  async init(ganttIDs, isProjectType) {
    this.isProjectType = isProjectType;

    this.ganttIDs = ganttIDs;
    this.updateAllColumns();
    this.loadJiraStatusesAndPriority();
    await this._loadColumnsConfig();
  }

  async _loadColumnsConfig() {
    await globalStore.dispatch('listView/defineCurrentConfig');
    const config = globalStore.getters['listView/currentConfig'];

    this.config = config ? config.columns : null;
    if (!this.config) return;

    const allowedShowCols = [];

    Object.keys(this.config).map(key => {
      const item = this.config[key];

      if (item.hidden) return;

      if (this.pricingAccesToCustomColumns && item.custom) {
        allowedShowCols.push(+key);
      }

      if (this.pricingAccesToDefaultColumns.includes(key)) {
        allowedShowCols.push(key);
      }

      if (this.pricingAccesToCost && costColumns.find(col => col.property === key)) {
        allowedShowCols.push(key);
      }

      if (item.showTime) {
        allowedShowCols.push(`${key}_extra`);
      }
    });

    this.showColumns = [...allowedShowCols];
  }

  _saveColumnsSettings({ columns, extra }) {
    const selecedColsName = columns.map(i => i.property);
    const columnsConfig = {};

    this.prependColumns.forEach(col => {
      const isShow = selecedColsName.find(c => c == col.property);

      columnsConfig[col.property] = {
        hidden: !isShow,
        custom: !!col.custom,
      };
    });

    this.staticColumns.forEach(col => {
      columnsConfig[col.property] = { hidden: false, custom: !!col.custom };
    });

    this.dynamicColumns.forEach(col => {
      const isShow = selecedColsName.find(c => c == col.property);

      columnsConfig[col.property] = {
        hidden: !isShow,
        custom: !!col.custom,
      };

      const ifExtra = extra.find(name => name.includes(col.property));

      if (ifExtra) {
        columnsConfig[col.property].showTime = true;
      }
    });

    globalStore.dispatch('listView/saveColumnsConfig', columnsConfig);
  }

  loadJiraStatusesAndPriority() {
    this.ganttIDs.forEach(ganttId => {
      const ganttData = projectsModel.getProjectDataById(ganttId);

      if (ganttData.is_jira) {
        this.statusCol.forJiraProject[ganttId] = globalStore.getters['columns/getJiraColumnByNameAndProjectId']('status', ganttId);
        this.priorityCol.forJiraProject[ganttId] = globalStore.getters['columns/getJiraColumnByNameAndProjectId']('priority', ganttId);
      }
    });
  }

  updateGanttIds(ganttIDs) {
    this.ganttIDs = [...ganttIDs];
  }

  changeColumnToShow({ columns, extra }) {
    this.showColumns.splice(0);
    this.showColumns.push(...extra);

    if (!columns.length) return;

    const properties = columns.map(c => c.property);

    this.showColumns.push(...properties);
    this._saveColumnsSettings({ columns, extra });
  }

  updateColumnById(columnId) {
    const curColumn = this.dynamicColumns.find(col => col.id === columnId);

    if (!curColumn) return;

    if (curColumn.custom) {
      const updCol = this._getPreparedCustomColumnById(columnId);

      updCol && Object.assign(curColumn, updCol);
    }
  }

  updateAllColumns() {
    const columns = defaultColumns.filter(col => {
      if (this.isProjectType && col.property === 'project_name') return false;

      if (this.pricingAccesToDefaultColumns.includes(col.property)) return col;

      return false;
    });

    if (this.pricingAccesToCustomColumns) {
      this.ganttIDs.forEach(ganttId => {
        const customColumns = this._getPreparedCustomColumnByProjectId(ganttId);

        customColumns.forEach(customCol => {
          const isAddedColumn = columns.find(col => col.id === customCol.id);

          !isAddedColumn && columns.push(customCol);
        });
      });
    }

    const addCostCol = this.ganttIDs.some(gantt_id => rights.project.hasCostRight(gantt_id));

    if (addCostCol && this.pricingAccesToCost) {
      columns.splice(6, 0, ...costColumns);
    }

    this.dynamicColumns.splice(0);
    this.dynamicColumns.push(...columns);
    this._checkShowedColumns();
  }

  _checkShowedColumns() {
    const toDelete = [];

    this.showColumns.forEach((col_name, index) => {
      if (typeof col_name === 'string' && col_name.includes('_extra')) { return; }

      if (!this.dynamicColumns.find(col => col.property === col_name)) { toDelete.push(index); }
    });

    toDelete.reverse().forEach(index => this.showColumns.splice(index, 1));
  }

  addCostColumns() {
    if (!this.pricingAccesToCost) return;
    if (this.dynamicColumns.find(c => c.id === 'total_price')) return;

    this.dynamicColumns.splice(6, 0, ...costColumns);
  }

  deleteCostColumns() {
    const indexes = [];
    const costNames = ['total_price', 'actual_cost'];

    this.dynamicColumns.filter((col, index) => {
      if (costNames.includes(col.id)) indexes.push(index);
    });

    indexes.reverse().forEach(index => {
      this.dynamicColumns.splice(index, 1);
    });

    const showCol = this.showColumns.filter(name => !costNames.includes(name));

    this.showColumns.splice(0);
    this.showColumns.push(...showCol);
  }

  addCustomColumnById(columnId) {
    this._updateCustomColumnToProjects();

    const column = this.dynamicColumns.find(col => col.id === columnId);

    if (column) return;

    const newCol = this._getPreparedCustomColumnById(columnId);

    newCol && this.dynamicColumns.push(newCol);
  }

  addCustomColumnsByProjectIDs(ganttIDs) {
    this._updateCustomColumnToProjects();

    ganttIDs.forEach(ganttId => {
      const customColumns = this._getPreparedCustomColumnByProjectId(ganttId);

      customColumns.forEach(column => {
        const addedColumn = this.dynamicColumns.find(col => col.id = column.id);

        !addedColumn && this.dynamicColumns.push(addedColumn);
      });
    });
  }

  deleteCustomColumnFromProjects(columnId, projectIDs) {
    const column = this.dynamicColumns.find(col => col.id === columnId);

    if (!column) return;

    if (!projectIDs || !projectIDs.length) {
      column.ganttIds = [];
    } else {
      column.ganttIds = column.ganttIds.filter(id => !projectIDs.includes(id));
    }

    !column.ganttIds.length && this._deleteCustomColumn(columnId);
  }

  deleteCustomColumnsByProjectIDs(ganttIDs) {
    this._updateCustomColumnToProjects();
  }

  _deleteCustomColumn(columnId) {
    const index = this.dynamicColumns.findIndex(col => col.id === columnId);

    if (index < 0) return;

    this.dynamicColumns.splice(index, 1);

    const isShowedCollIndex = this.showColumns.findIndex(col => col == columnId);

    isShowedCollIndex >= 0 && this.showColumns.splice(isShowedCollIndex, 1);
  }

  _updateCustomColumnToProjects() {
    const indexes = [];

    this.dynamicColumns.forEach((column, index) => {
      if (!column.custom) return;

      const ganttIds = globalStore.getters['columns/getProjectsByColumnId'](column.id);

      column.ganttIds = ganttIds.filter(id => this.ganttIDs.includes(id));

      if (!column.ganttIds.length) { indexes.push(index); }
    });

    indexes.reverse().forEach(index => {
      this.dynamicColumns.splice(index, 1);
    });
  }

  _getPreparedCustomColumnByProjectId(ganttID) {
    const projectColumnIds = globalStore.getters['columns/getColumnIdsByProjectId'](ganttID);

    const customColumns = [];

    projectColumnIds.forEach(columnId => {
      const column = this._getPreparedCustomColumnById(columnId);

      column && customColumns.push(column);
    });

    return customColumns;
  }

  _getPreparedCustomColumnById(columnId) {
    const column = getPreparedCustomColumnById(columnId);

    if (!column) {
      return null;
    }

    column.ganttIds = column.ganttIds.filter(id => this.ganttIDs.includes(id));
    column.tooltipExtra = this._createTooltipInfoForCustomCol(columnId);
    column.resizable = true;

    return column;
  }

  _createTooltipInfoForCustomCol(col_id) {
    const projectIds = globalStore.getters['columns/getProjectsByColumnId'](col_id);

    const projectsNameHtml = this.ganttIDs.map(gantt_id => {
      if (projectIds.includes(+gantt_id)) {
        const project = projectsModel.getProjectDataById(+gantt_id);

        if (project) {
          return `<li class='tooltip-list-item'>${project.name}</li>`;
        }
      }

      return '';
    });

    return {
      header: __('guntt_custom_columns_multiview_tooltip_project'),
      descriptionHtml: projectsNameHtml.join(''),
    };
  }
}

export default TableColumns;
